/**
 * Customer-facing copy for password-reset failures.
 *
 * The Forgot Password dialog used to render Supabase's error string verbatim.
 * Those strings are written for developers ("For security purposes, you can
 * only request this after 42 seconds", "Unable to validate email address:
 * invalid format") and a customer reading one mid-panic paraphrases it into
 * something else entirely — Opal Oakes (2026-09-04) reported "it says my email
 * is not found" for a failure our stack cannot actually produce with that
 * wording. Whatever she saw, the raw text turned a transient blip into a
 * support ticket.
 *
 * Classification is by HTTP status and Supabase error code first, message
 * text last, and the raw message is never returned to the caller. Every
 * branch says what to DO next, because "something failed" with no next step
 * is what sends people to the sign-up form to try again from scratch.
 */

export const RESET_SUPPORT_LINE = 'Still stuck? Send us a message from defera.app/contact and we\'ll send you a link directly.';

/** @returns {{ title: string, detail: string, retryAfterSeconds: number | null }} */
export function describeResetError(err) {
  const status = Number(err?.status) || 0;
  const code = String(err?.code || '').toLowerCase();
  const msg = String(err?.message || '').toLowerCase();

  // Too many requests for this address in a short window. Supabase sends the
  // first one and refuses the repeat — so the useful advice is "it's probably
  // already on its way", not "try again".
  if (status === 429 || code.includes('rate_limit') || code === 'over_email_send_rate_limit' || /rate limit|security purposes|only request this after/.test(msg)) {
    const secs = Number((msg.match(/after (\d+) second/) || [])[1]) || null;
    return {
      title: 'We just sent one',
      detail: `A reset link was requested for this address a moment ago — check your inbox and spam folder before asking for another${secs ? `, or wait about ${secs} seconds and try again` : ''}.`,
      retryAfterSeconds: secs,
    };
  }

  if (code === 'validation_failed' || /validate email|invalid format|invalid email/.test(msg)) {
    return {
      title: 'That email doesn\'t look right',
      detail: 'Check for typos or a stray space, then try again.',
      retryAfterSeconds: null,
    };
  }

  // Browser could not reach Supabase at all (offline, DNS, blocked).
  if (status === 0 || code === 'fetch_error' || /failed to fetch|networkerror|network request failed|load failed/.test(msg)) {
    return {
      title: 'We couldn\'t reach our servers',
      detail: 'Check your connection and try again in a moment. Nothing was sent.',
      retryAfterSeconds: null,
    };
  }

  return {
    title: 'We couldn\'t send the link just now',
    detail: 'This is usually temporary. Wait a minute and try again — and if you already requested one today, check your spam folder first.',
    retryAfterSeconds: null,
  };
}

/**
 * Customer-facing copy for a recovery link that fails at /auth/callback.
 *
 * Margo Dixon (LXUS6Y, 2026-09-17) requested three resets in seven minutes and
 * was told "the link is expired" each time. The links were not expired: each
 * one was consumed 34, 36 and 61 seconds after it was sent, before she ever
 * tapped it. Supabase burns a recovery token on the first touch of the link,
 * and email security scanners follow links automatically on delivery — across
 * the whole userbase, 259 of 557 recovery links were consumed within 15
 * seconds of being sent, and only 73 password changes ever completed.
 *
 * She saw Supabase's own `error_description` rendered verbatim ("Email link is
 * invalid or has expired"), which reads as "wait for a fresh email" — so she
 * requested another, and the loop continued. Same rule as describeResetError:
 * classify, never echo the raw string, and always say what to do next.
 *
 * @returns {{ title: string, detail: string, canRequestNew: boolean }}
 */
export function describeRecoveryLinkError(input) {
  const code = String(input?.errorCode || input?.code || '').toLowerCase();
  const msg = String(input?.errorDescription || input?.message || '').toLowerCase();

  // Already spent. Overwhelmingly a scanner got there first, or the customer
  // requested a newer link (which invalidates the older one) and opened the
  // older email.
  if (
    code === 'otp_expired'
    || /expired|already been used|already used|token not found|invalid token/.test(msg)
  ) {
    return {
      title: 'That reset link has already been used',
      detail: 'Reset links work once, and some email apps open links automatically before you get to them. Request a fresh one and open it as soon as it arrives — and if more than one reset email is sitting in your inbox, use the newest.',
      canRequestNew: true,
    };
  }

  // PKCE: the code has to be exchanged in the browser that asked for it.
  // Tapping a link from a mail app often opens a different browser context.
  if (/code verifier|code_verifier|pkce|both auth code and code verifier/.test(msg)) {
    return {
      title: 'Finish this on the device you requested it from',
      detail: 'This link has to be opened in the same browser that asked for the reset. Request a fresh link from the device you are on now, then open it here.',
      canRequestNew: true,
    };
  }

  if (code === 'access_denied' || /access denied|not authorized/.test(msg)) {
    return {
      title: 'This link is no longer valid',
      detail: 'Request a fresh reset link and open it as soon as it arrives.',
      canRequestNew: true,
    };
  }

  return {
    title: 'We couldn\'t open that reset link',
    detail: 'Request a fresh link and open it as soon as it arrives. If it keeps failing, send us a message from defera.app/contact and we\'ll get you in.',
    canRequestNew: true,
  };
}
